import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import { imgList } from "@constants/imgList";
import { chartColors } from "@constants/colors";
import {
  MetricDetailLayout,
  MetricFilterControls,
  SummaryBlock,
  useDeviceReport,
  timeColumn,
  numericColumn,
} from "@components/MetricDetail";
import type { FetchReport } from "@components/MetricDetail";
import Excel from "@components/Excel";
import Table from "@components/Table";
import ChartMetricLine from "@components/Charts/ChartMetricLine";
import ChartPlaceholder from "@components/Charts/ChartPlaceholder";
import { displayImage } from "@utils/image";
import { localAnchorStr } from "@utils/date";
import { apiGetReportWindow } from "@apis/reportWindow";
import { toFixed, toFixedOrDash } from "@utils/string";
import { runReportAggregation } from "@utils/reportTableWorkerClient";
import { sumPowerUsage } from "@utils/deviceReportService";
import {
  computeStats,
  downsampleAvg,
  recommendedResolution,
} from "@utils/temperatureHistory";

// numeric fields averaged per resolution window (see downsampleAvg)
const POWER_NUMERIC_KEYS = ["voltage", "current", "power", "kw"];

// Section glyphs — same set the temperature page uses.
const DownloadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const ChartLineIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FEBC11"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19l16 0" />
    <path d="M4 15l4 -6l4 2l4 -5l4 4" />
  </svg>
);
const TableIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FEBC11"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14z" />
    <path d="M3 10h18" />
    <path d="M10 3v18" />
  </svg>
);

export default function PowerDetail() {
  const [dataTemp, setDataTemp] = useState<any>({});
  // Display resolution (minutes) for the charts + table. Auto-set to the
  // recommended value for the selected range; the user can override via chips.
  const [resolution, setResolution] = useState(1);
  const getReportPower: FetchReport = async (
    filterDeviceDetail,
    language,
    id
  ) => {
    const res = await apiGetReportWindow(
      (anchor: string) =>
        `${
          process.env.REACT_APP_API_DOMAIN
        }/monitoring-api/device-report/${id}/power?summarize=false&dateTime=${anchor}&dateTimeUnit=${filterDeviceDetail.unit}`,
      localAnchorStr(filterDeviceDetail.date, filterDeviceDetail.period),
      filterDeviceDetail.unit,
      language
    );
    if (res.status && res.code === 200) {
      const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
        "YYYY-MM-DD"
      )} ${
        filterDeviceDetail.period.toString().length < 2
          ? `0${filterDeviceDetail.period}:00:00`
          : `${filterDeviceDetail.period}:00:00`
      }`;
      const results = await runReportAggregation("power", {
        rawResult: res?.data?.data?.result ?? [],
        dateTimeStr,
        unit: filterDeviceDetail.unit,
      });
      setDataTemp({ results });
    }
  };
  const {
    t,
    navigate,
    id,
    deviceDetail,
    filterDeviceDetail,
    plan,
    listUnit,
    listPeriod,
    renderSound,
  } = useDeviceReport(getReportPower);

  // At-a-glance summary — kWh usage + cost via the shared sumPowerUsage (same
  // math the device-detail quick view uses), plus voltage/current/power averages
  // via computeStats (null-filtered per the null-vs-0 rule).
  const stat = (key: string) =>
    computeStats(
      (dataTemp?.results ?? [])
        .map((r: any) => ({
          time: r?.time ?? "",
          value:
            r?.[key] === null || r?.[key] === undefined || r?.[key] === ""
              ? NaN
              : Number(r?.[key]),
        }))
        .filter((r: any) => Number.isFinite(r.value))
    );
  const summary = useMemo(() => {
    const rows: any[] = dataTemp?.results ?? [];
    if (rows.length === 0) return null;
    const usage = sumPowerUsage(rows);
    const voltage = stat("voltage");
    const power = stat("power");
    const first = rows[0];
    const last = rows[rows.length - 1];
    const diffMin = Math.max(
      0,
      dayjs(last?.time).diff(dayjs(first?.time), "minute")
    );
    return {
      usage,
      voltage,
      power,
      diffMin,
      from: first?.time ?? "",
      to: last?.time ?? "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTemp]);
  const durationLabel = summary
    ? summary.diffMin >= 60
      ? `${Math.floor(summary.diffMin / 60)} ${t("detail.hour")} ${
          summary.diffMin % 60
        } ${t("detail.minunit")}`
      : `${summary.diffMin} ${t("detail.minunit")}`
    : "";
  const periodLabel = t(
    `detail.period${filterDeviceDetail.unit}` as any
  ) as string;

  // Auto-snap the display resolution to the recommended value when the report
  // range changes (hours/days→1m, weeks→5m, months→30m); a manual chip pick
  // then sticks until the range changes again.
  useEffect(() => {
    setResolution(recommendedResolution(filterDeviceDetail.unit));
  }, [filterDeviceDetail.unit]);

  // Charts + table + Excel all render these downsampled rows — numeric power
  // fields averaged per window (divide by the actual sample count; gaps kept
  // as null). Summary stats above stay on the RAW rows (resolution-independent).
  const displayRows: any[] = useMemo(
    () =>
      downsampleAvg(
        (dataTemp?.results ?? []) as any[],
        resolution,
        POWER_NUMERIC_KEYS
      ),
    [dataTemp, resolution]
  );

  // One brush (on the bottom chart) drives the zoom window for all three: the
  // top two charts render the already-sliced window, so a single slider scrolls
  // them in lockstep. Reset when the underlying rows change (unit/resolution).
  const [brush, setBrush] = useState<{
    startIndex: number;
    endIndex: number;
  } | null>(null);
  useEffect(() => {
    setBrush(null);
  }, [displayRows]);
  const viewedRows = useMemo(
    () =>
      brush
        ? displayRows.slice(brush.startIndex, brush.endIndex + 1)
        : displayRows,
    [displayRows, brush]
  );

  const RESOLUTIONS = [1, 5, 10, 30, 60];
  const columns = [
    timeColumn(t),
    numericColumn(t, "detail.vol", "voltage"),
    numericColumn(t, "detail.amp", "current"),
    numericColumn(t, "detail.watt", "power"),
    numericColumn(t, "detail.khw", "kw"),
    numericColumn(t, "detail.mode", "mode"),
  ];

  // ── Excel: 2 sheets (summary / readings), mirroring the temperature page ──
  const F = t("detail.xlsfield");
  const V = t("detail.xlsvalue");
  const kv = (label: string, value: any) => ({ [F]: label, [V]: value });
  const excelInfoData = summary
    ? [
        kv(t("detail.name"), deviceDetail?.deviceInfo?.name ?? "-"),
        kv(
          t("detail.mac"),
          deviceDetail?.controlInfo?.macAddress ??
            deviceDetail?.deviceInfo?.macAddress ??
            "-"
        ),
        kv(t("monitoring.location"), deviceDetail?.name ?? "-"),
        kv(t("detail.reporttype"), periodLabel),
        kv(
          t("detail.dataperiod"),
          `${dayjs(summary.from).format("DD/MM/YYYY HH:mm")} - ${dayjs(
            summary.to
          ).format("DD/MM/YYYY HH:mm")} (${durationLabel})`
        ),
        kv(
          t("detail.electunit"),
          `${toFixed(summary.usage.electricityUnit ?? 0, 2)} ${t(
            "detail.unit2"
          )}`
        ),
        kv(
          t("detail.electbill"),
          `${toFixed(summary.usage.electricityBill ?? 0, 2)} ${t("detail.baht")}`
        ),
        kv(
          t("detail.carbonfootprint"),
          `${toFixed(summary.usage.electricityCarbon ?? 0, 2)} ${t(
            "detail.carbonunit"
          )}`
        ),
        // the unit rides with the value here, like the rows above — the sheet
        // has no second line to put it on the way the on-screen tile does
        kv(
          `${t("detail.volname")} (${t("detail.average")})`,
          summary.voltage.avg === null
            ? "-"
            : `${toFixed(summary.voltage.avg, 1)} ${t("monitoring.volunit")}`
        ),
        kv(
          `${t("detail.wattname")} (${t("detail.average")})`,
          summary.power.avg === null
            ? "-"
            : `${toFixed(summary.power.avg, 1)} W`
        ),
        kv(t("detail.printedat"), dayjs().format("DD/MM/YYYY HH:mm")),
      ]
    : [];
  const excelReadingHeader = [
    t("detail.time"),
    t("detail.vol"),
    t("detail.amp"),
    t("detail.watt"),
    t("detail.khw"),
  ];
  const excelReadingData = displayRows.map((e: any) => ({
    Time: dayjs(e?.time ?? "").format("DD/MM/YYYY HH:mm"),
    Voltage: toFixedOrDash(e?.voltage),
    Amp: toFixedOrDash(e?.current),
    Watt: toFixedOrDash(e?.power),
    KWH: toFixedOrDash(e?.kw),
  }));
  const excelSheets = [
    { name: t("detail.xlsinfo"), data: excelInfoData },
    {
      name: t("detail.powerdetail"),
      header: excelReadingHeader,
      data: excelReadingData,
    },
  ];

  return (
    <MetricDetailLayout
      title={t("detail.powerdetail")}
      metricLabel={t("detail.power")}
      devicesLabel={t("devices.head")}
      onBack={() => navigate(`/device-detail/${id}`)}
      onBreadcrumbDevices={() => navigate(`/devices`)}
      deviceName={deviceDetail?.deviceInfo?.name ?? ""}
      image={
        (deviceDetail?.deviceInfo?.image ?? "")
          ? displayImage(deviceDetail?.deviceInfo?.image ?? "")
          : imgList.ex
      }
      locationName={deviceDetail?.name ?? ""}
      soundSlot={renderSound(
        deviceDetail?.controlInfo?.macAddress ?? "",
        deviceDetail?.controlInfo?.soundAlarm ?? false,
        deviceDetail?.controlInfo?.control?.alarm ?? false
      )}
      headerActions={
        <Excel
          fileName={`${id}-${t("detail.power")}-${dayjs().format(
            "YYYYMMDDHHmmss"
          )}`}
          sheets={excelSheets as any}
          button={
            <div
              className="size-14 weight-600 color-black pointer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid #e2e8f0",
                padding: "6px 14px",
                borderRadius: 8,
                whiteSpace: "nowrap",
              }}
            >
              <DownloadIcon />
              {t("detail.downloadexcel")}
            </div>
          }
        />
      }
      breadcrumbActions={
        <MetricFilterControls
          filterDeviceDetail={filterDeviceDetail}
          plan={plan}
          listUnit={listUnit}
          listPeriod={listPeriod}
        />
      }
      headlineExtra={
        summary ? (
          // Mirrors the temperature/operation summary chrome (SummaryBlock):
          // kWh usage + cost + voltage/power averages, then the report-type /
          // recording-period line.
          <SummaryBlock>
            <div className="sum__head">{t("detail.powersummary")}</div>
            <div className="sum__row">
              <div className="sum__center">
                <div className="sum__tiles">
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.electunit")}
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {toFixed(summary.usage.electricityUnit ?? 0, 2)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("detail.unit2")}
                      </span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.electbill")}
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {toFixed(summary.usage.electricityBill ?? 0, 2)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("detail.baht")}
                      </span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.carbonfootprint")}
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {toFixed(summary.usage.electricityCarbon ?? 0, 2)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("detail.carbonunit")}
                      </span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.volname")} ({t("detail.average")})
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {summary.voltage.avg === null
                        ? "-"
                        : toFixed(summary.voltage.avg, 1)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("monitoring.volunit")}
                      </span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.wattname")} ({t("detail.average")})
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {summary.power.avg === null
                        ? "-"
                        : toFixed(summary.power.avg, 1)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>W</span>
                    </div>
                  </div>
                </div>
                <div className="sum__duration">
                  {t("detail.reporttype")}: {periodLabel}
                  {"  ·  "}
                  {t("detail.dataperiod")}: {durationLabel}
                </div>
              </div>
            </div>
          </SummaryBlock>
        ) : null
      }
      filterBar={
        <div className="detail__filter">
          {/* chart section heading + resolution chips (drive charts + table +
              Excel together) — matches the temperature page's arrangement */}
          <div
            className="detail__head-2"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <ChartLineIcon />
            <span className="color-black size-20 font-sukhumvitSet-bold">
              {t("detail.powerchart")}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginLeft: "auto",
            }}
          >
            {RESOLUTIONS.map((r) => (
              <div
                key={r}
                className="pointer"
                onClick={() => setResolution(r)}
                style={{
                  padding: "4px 14px",
                  borderRadius: 16,
                  border: `1px solid ${
                    resolution === r ? "#FEBC11" : "#e2e8f0"
                  }`,
                  background: resolution === r ? "#fffbeb" : "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  color: resolution === r ? "#b45309" : "#64748b",
                }}
              >
                {r >= 60
                  ? `${r / 60} ${t("detail.hour")}`
                  : `${r} ${t("detail.minunit")}`}
              </div>
            ))}
          </div>
        </div>
      }
      chart={
        displayRows.length > 0 ? (
          <div>
            {/* top two charts render the brushed window (sliced); only the
                bottom chart shows the single shared slider */}
            {/* top two charts render the brushed window (sliced); only the
                bottom chart shows the single shared slider. Compact heights so
                the three stacked charts read like the temperature page. */}
            <ChartMetricLine
              data={viewedRows}
              dataKey="voltage"
              name={t("detail.vol")}
              color={chartColors.voltage}
              unitSuffix=" V"
              height={210}
              showBrush={false}
            />
            <ChartMetricLine
              data={viewedRows}
              dataKey="current"
              name={t("detail.amp")}
              color={chartColors.current}
              unitSuffix=" A"
              height={210}
              showBrush={false}
            />
            <ChartMetricLine
              data={displayRows}
              dataKey="power"
              name={t("detail.watt")}
              color={chartColors.power}
              unitSuffix=" W"
              height={210}
              brushStartIndex={brush?.startIndex}
              brushEndIndex={brush?.endIndex}
              onBrushChange={setBrush}
            />
          </div>
        ) : (
          <ChartPlaceholder
            loading={!Array.isArray(dataTemp?.results)}
            loadingText={t("detail.loadingdata")}
            emptyText={t("detail.nodatarange")}
          />
        )
      }
      table={
        <div className="detail__table">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "8px 0 16px",
            }}
          >
            <TableIcon />
            <span className="color-black size-20 font-sukhumvitSet-bold">
              {t("detail.powertable")}
            </span>
          </div>
          <Table
            pagination={{
              locale: { items_per_page: t("detail.perpage") },
              showLessItems: true,
              showTotal: (total: number, range: [number, number]) =>
                `${t("detail.showing")} ${range[0].toLocaleString(
                  "en-US"
                )}-${range[1].toLocaleString("en-US")} ${t(
                  "detail.oftotal"
                )} ${total.toLocaleString("en-US")} ${t("detail.rowunit")}`,
            }}
            showSorterTooltip={false}
            columns={columns}
            dataSource={displayRows.map((e: any, index: number) => {
              return {
                key: index,
                time: dayjs(e?.time ?? "").format("DD/MM/YYYY HH:mm"),
                voltage: toFixedOrDash(e?.voltage),
                current: toFixedOrDash(e?.current),
                power: toFixedOrDash(e?.power),
                kw: toFixedOrDash(e?.kw),
                mode: "-",
              };
            })}
          />
        </div>
      }
    />
  );
}
